// The parts every record page shares, so each one reads the same way and needs no instructions:
// - ActionBar: the next step first and filled, the other steps in process order, then tools; cancel and
//   other risky actions sit under More, never next to the step people click every day.
// - RecordHeader: what the record is (kicker, number, title, status), the few facts that matter, the stage
//   track, and a "Next step" line that says in one sentence what happens next, with its button.
// - Meter: a quantity against its whole ("200 of 2,400 shipped") as a thin bar, so progress is seen, not read.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

export type Act = {
  label: string; icon?: ReactNode; onClick?: () => void; to?: string; href?: string;
  disabled?: boolean; title?: string; danger?: boolean; hidden?: boolean;
};

function ActButton({ a, kind, narrow }: { a: Act; kind: "primary" | "step" | "tool" | "menu"; narrow?: boolean }) {
  const cls = kind === "primary" ? "act act-primary" : kind === "menu" ? `menu-item${a.danger ? " danger" : ""}${narrow ? " narrow" : ""}` : `act${kind === "tool" ? " act-tool" : ""} wide`;
  const body = <>{a.icon}<span>{a.label}</span>{a.href && <span className="ext" aria-hidden="true">↗</span>}</>;
  if (a.to) return <Link className={cls} to={a.to} title={a.title} role={kind === "menu" ? "menuitem" : undefined}>{body}</Link>;
  if (a.href) return <a className={cls} href={a.href} target="_blank" rel="noopener noreferrer" title={a.title} role={kind === "menu" ? "menuitem" : undefined}>{body}</a>;
  return <button className={cls} onClick={a.onClick} disabled={a.disabled} title={a.title} role={kind === "menu" ? "menuitem" : undefined}>{body}</button>;
}

/** The record's action bar: next step, other steps, tools, and a More menu. */
export function ActionBar({ primary, steps = [], tools = [], more = [], status }: { primary?: Act | null; steps?: Act[]; tools?: Act[]; more?: Act[]; status?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => { if (e instanceof KeyboardEvent ? e.key === "Escape" : !ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close); document.addEventListener("keydown", close);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", close); };
  }, [open]);
  const s = steps.filter((a) => !a.hidden), t = tools.filter((a) => !a.hidden), m = more.filter((a) => !a.hidden);
  return (
    <div className="actbar" role="toolbar" aria-label="Actions">
      {primary && !primary.hidden && <ActButton a={primary} kind="primary" />}
      {s.map((a) => <ActButton key={a.label} a={a} kind="step" />)}
      {t.length > 0 && (s.length > 0 || primary) && <span className="act-sep wide" aria-hidden="true" />}
      {t.map((a) => <ActButton key={a.label} a={a} kind="tool" />)}
      <span className="spacer" />
      {status && <span className="act-status wide">{status}</span>}
      {/* On a phone only the next step stays in the bar; the other steps and tools move into More. */}
      {(m.length > 0 || s.length > 0 || t.length > 0) && (
        <div className={`more ${m.length ? "" : "narrow-only"}`} ref={ref}>
          <button className="act act-tool" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>More<svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true"><path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" /></svg></button>
          {open && <div className="menu" role="menu" onClick={() => setOpen(false)}>
            {s.map((a) => <ActButton key={`s-${a.label}`} a={a} kind="menu" narrow />)}
            {t.map((a) => <ActButton key={`t-${a.label}`} a={a} kind="menu" narrow />)}
            {(s.length > 0 || t.length > 0) && m.length > 0 && <span className="menu-sep narrow" aria-hidden="true" />}
            {m.map((a) => <ActButton key={a.label} a={a} kind="menu" />)}
          </div>}
        </div>
      )}
    </div>
  );
}

export type Fact = { label: string; value: ReactNode; note?: ReactNode; tone?: "bad" | "warn" | "good" };
export type Stage = { id: string; name: string };

/** Top of a record: identity, key facts, the stage track and the next step. */
export function RecordHeader({ crumb, kicker, number, title, status, facts = [], stages, current, closed, next }: {
  crumb?: { to: string; label: string }; kicker: string; number: string; title: ReactNode;
  status: { label: string; tone?: "ok" | "warn" | "info" | "danger" | "" };
  facts?: Fact[]; stages?: Stage[]; current?: number; closed?: string | null;
  next?: { text: ReactNode; action?: Act | null; tone?: "info" | "warn" | "done" } | null;
}) {
  return (
    <section className="rhead" aria-label={`${kicker} ${number}`}>
      <div className="rhead-crumb">{crumb ? <Link to={crumb.to}>{crumb.label}</Link> : <span>{kicker}</span>}<span aria-hidden="true">/</span><span className="mono">{number}</span></div>
      <div className="rhead-id">
        <h1>{title}</h1>
        <span className={`pill ${status.tone ?? ""}`}>{status.label}</span>
      </div>
      {facts.length > 0 && (
        <dl className="rhead-facts">
          {facts.map((f) => <div key={f.label}><dt>{f.label}</dt><dd className={f.tone ?? ""}>{f.value}{f.note && <span className="rhead-note">{f.note}</span>}</dd></div>)}
        </dl>
      )}
      {stages && current !== undefined && (
        <ol className="steps" aria-label="Stages">
          {stages.map((st, i) => {
            const done = i < current || (i === current && closed === "done");
            return <li key={st.id} className={done ? "done" : i === current && !closed ? "cur" : ""} aria-current={i === current && !closed ? "step" : undefined}>{done ? "✓ " : ""}{st.name}</li>;
          })}
          {closed && closed !== "done" && <li className="cancelled">{closed}</li>}
        </ol>
      )}
      {next && (
        <div className={`nextstep ${next.tone ?? "info"}`} role="status">
          <span className="nextstep-l">{next.tone === "done" ? "Done" : "Next step"}</span>
          <span className="nextstep-t">{next.text}</span>
          {next.action && !next.action.hidden && <ActButton a={next.action} kind={next.tone === "done" ? "tool" : "primary"} />}
        </div>
      )}
    </section>
  );
}

/** A quantity against its whole, as a thin bar with the figures beside it. */
export function Meter({ value, max, label, tone }: { value: number; max: number; label?: ReactNode; tone?: "ok" | "warn" | "bad" }) {
  const p = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
  return (
    <span className={`meter ${tone ?? ""}`} role="meter" aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} aria-label={typeof label === "string" ? label : undefined}>
      <span className="meter-bar"><span style={{ width: `${p * 100}%` }} /></span>
      {label !== undefined && <span className="meter-l">{label}</span>}
    </span>
  );
}
